import { Box, Button, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { FunctionComponent, useContext, useEffect } from 'react';
import { BI } from '../../../constants';
import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { ArticleSearchResult } from '../../../types';
import { isMobile } from '../../../utils/userAgent';
import { Breadcrumbs } from '../../Breadcrumbs';
import css from './index.module.scss';
import { gotNoAnswer } from '@wix/bi-logger-new-help-center/v2';

export type NoResultsFoundProps = {
  term: string;
  data?: Pick<ArticleSearchResult, 'searchMethod' | 'searchVersion'> | null;
};

export const NoResultsFound: FunctionComponent<NoResultsFoundProps> = ({
  term,
  data,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const context = useContext(Context);
  const { sendBIEvent } = useBI();

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];

  useEffect(() => {
    void (async () => {
      await sendBIEvent(
        gotNoAnswer({
          kb_lang: locale as string,
          source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
          search_method: data?.searchMethod,
          search_version: data?.searchVersion,
          question: term,
        })
      );
    })();
  }, [data, locale, term]);

  return (
    <>
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      <Box
        className={css.NoResultsFound}
        direction="vertical"
        verticalAlign="middle"
        align="center"
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND}
      >
        <Heading
          appearance="H2"
          dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_TITLE}
          className={css.title}
        >
          {t(`search-results-page.no-results-found.title`, { term })}
        </Heading>
        <Text
          weight="thin"
          size="medium"
          dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_DESCRIPTION}
          className={css.description}
        >
          {t('search-results-page.no-results-found.description')}
        </Text>
        <Button
          size={isMobile(context) ? 'small' : 'medium'}
          as="a"
          href={`/${locale}`}
          skin="standard"
          dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_CTA}
        >
          {t('search-results-page.no-results-found.return-to-homepage')}
        </Button>
      </Box>
    </>
  );
};
